import {
  SET_CHAT_CONVERSATION,
  SET_CHAT_MESSAGES,
  UPSERT_CHAT_MESSAGE,
  SET_CHAT_PRESENCE,
  APPLY_CHAT_READ_RECEIPT,
  RESET_CHAT,
  SET_CHAT_INBOX,
  UPSERT_INBOX_ITEM,
  PREPEND_CHAT_MESSAGES,
  APPEND_CHAT_MESSAGES,
  SET_TOTAL_UNREAD,
  SET_TEAM_INBOX,
  UPSERT_TEAM_INBOX_ITEM,
  SET_TEAM_CONVERSATION,
  SET_TEAM_MESSAGES,
  UPSERT_TEAM_MESSAGE,
  APPEND_TEAM_MESSAGES,
  APPLY_TEAM_READ_RECEIPT,
  SET_TEAM_TOTAL_UNREAD,
} from '../Types/types';

const initialState = {
  conversation: null,
  messages: [],
  inbox: [],
  totalUnread: 0,
  teamInbox: [],
  teamConversation: null,
  teamMessages: [],
  teamTotalUnread: 0,
};

export default function chatReducer(state = initialState, action) {
  switch (action.type) {
    case SET_CHAT_CONVERSATION:
      return { ...state, conversation: action.payload };

    case SET_CHAT_MESSAGES:
      return { ...state, messages: action.payload };

    case PREPEND_CHAT_MESSAGES:
      return { ...state, messages: [...action.payload, ...state.messages] };

    case APPEND_CHAT_MESSAGES:
      return { ...state, messages: [...state.messages, ...action.payload] };

    case UPSERT_CHAT_MESSAGE: {
      const msg = action.payload;
      const idx = state.messages.findIndex((m) => m.id === msg.id);
      let updated;
      if (idx >= 0) {
        updated = state.messages.map((m, i) => (i === idx ? { ...m, ...msg } : m));
      } else {
        updated = [...state.messages, msg].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
      }
      return { ...state, messages: updated };
    }

    case SET_CHAT_PRESENCE:
      if (!state.conversation) return state;
      return {
        ...state,
        conversation: {
          ...state.conversation,
          recruiter_online: action.payload.recruiter_online,
          candidate_online: action.payload.candidate_online,
        },
      };

    case APPLY_CHAT_READ_RECEIPT: {
      const { sender_type, last_read_message_id } = action.payload;
      const cutoffIdx = state.messages.findIndex((m) => m.id === last_read_message_id);
      if (cutoffIdx < 0) return state;
      const cutoffTime = new Date(state.messages[cutoffIdx].created_at);
      const updated = state.messages.map((m) => {
        if (m.sender_type !== sender_type) return m;
        return { ...m, is_read_by_other: new Date(m.created_at) <= cutoffTime };
      });
      return { ...state, messages: updated };
    }

    case SET_CHAT_INBOX:
      return { ...state, inbox: action.payload };

    case UPSERT_INBOX_ITEM: {
      const item = action.payload;
      if (!item?.job_candidate_id) return state;
      const key = String(item.job_candidate_id);
      const idx = state.inbox.findIndex((r) => String(r.job_candidate_id) === key);
      let updated;
      if (idx >= 0) {
        const definedFields = Object.fromEntries(
          Object.entries(item).filter(([, v]) => v !== undefined && v !== null),
        );
        updated = state.inbox.map((r, i) => (i === idx ? { ...r, ...definedFields } : r));
      } else {
        updated = [...state.inbox, { ...item, last_message_at: item.last_message_at || new Date().toISOString() }];
      }
      updated = [...updated].sort((a, b) => {
        const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return tb - ta;
      });
      return { ...state, inbox: updated };
    }

    case SET_TOTAL_UNREAD:
      return { ...state, totalUnread: action.payload };

    case RESET_CHAT:
      return initialState;

    case SET_TEAM_INBOX: {
      const items = action.payload || [];
      const teamTotalUnread = items.reduce((sum, r) => sum + (r.unread_count || 0), 0);
      return { ...state, teamInbox: items, teamTotalUnread };
    }

    case UPSERT_TEAM_INBOX_ITEM: {
      const item = action.payload;
      if (!item?.conversation_id) return state;
      const key = String(item.conversation_id);
      const idx = state.teamInbox.findIndex((r) => String(r.conversation_id) === key);
      let updated;
      if (idx >= 0) {
        const defined = Object.fromEntries(Object.entries(item).filter(([, v]) => v !== undefined && v !== null));
        updated = state.teamInbox.map((r, i) => (i === idx ? { ...r, ...defined } : r));
      } else {
        updated = [...state.teamInbox, { ...item, last_message_at: item.last_message_at || new Date().toISOString() }];
      }
      updated = [...updated].sort((a, b) => {
        const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return tb - ta;
      });
      const teamTotalUnread = updated.reduce((sum, r) => sum + (r.unread_count || 0), 0);
      return { ...state, teamInbox: updated, teamTotalUnread };
    }

    case SET_TEAM_CONVERSATION:
      return { ...state, teamConversation: action.payload };

    case SET_TEAM_MESSAGES:
      return { ...state, teamMessages: action.payload };

    case APPEND_TEAM_MESSAGES:
      return { ...state, teamMessages: [...state.teamMessages, ...action.payload] };

    case UPSERT_TEAM_MESSAGE: {
      const msg = action.payload;
      const idx = state.teamMessages.findIndex((m) => m.id === msg.id);
      let updated;
      if (idx >= 0) {
        const existing = state.teamMessages[idx];
        const incomingAtt = msg.attachments;
        const existingAtt = existing.attachments;
        let attachments;
        if (Array.isArray(incomingAtt) && incomingAtt.length > 0) {
          attachments = incomingAtt;
        } else if (Array.isArray(existingAtt) && existingAtt.length > 0) {
          attachments = existingAtt;
        } else {
          attachments = Array.isArray(incomingAtt) ? incomingAtt : (existingAtt || []);
        }
        updated = state.teamMessages.map((m, i) => (i === idx ? { ...existing, ...msg, attachments } : m));
      } else {
        updated = [...state.teamMessages, {
          attachments: Array.isArray(msg.attachments) ? msg.attachments : [],
          ...msg,
        }].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      }
      return { ...state, teamMessages: updated };
    }

    case APPLY_TEAM_READ_RECEIPT: {
      const { user_id, last_read_message_id, last_read_at } = action.payload;
      const cutoffIdx = state.teamMessages.findIndex((m) => m.id === last_read_message_id);
      let cutoff;
      if (cutoffIdx >= 0) {
        cutoff = new Date(state.teamMessages[cutoffIdx].created_at);
      } else if (last_read_at) {
        cutoff = new Date(last_read_at);
      } else {
        return state;
      }
      const currentUserId = (() => { try { const t = localStorage.getItem('token') || ''; const p = JSON.parse(atob(t.split('.')[1])); return Number(p.user_id ?? p.UserId ?? p.userId ?? 0); } catch { return 0; } })();
      if (Number(user_id) === currentUserId) return state;
      const updated = state.teamMessages.map((m) => {
        if (m.message_type !== 'user') return m;
        const sId = Number(m.sender_user_id ?? 0);
        if (sId !== currentUserId) return m;
        return { ...m, is_read_by_others: new Date(m.created_at) <= cutoff };
      });
      return { ...state, teamMessages: updated };
    }

    case SET_TEAM_TOTAL_UNREAD:
      return { ...state, teamTotalUnread: action.payload };

    default:
      return state;
  }
}
